import type { WatchStopHandle } from 'vue'
import { v4 as uuid } from 'uuid'


function sleep(times: number) {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve(undefined)
    }, times)
  })
}

function wait(checkFunc: () => boolean) {
  return new Promise<void>((resolve) => {
    let stopOnFirst = false

    let stop: WatchStopHandle | undefined = undefined
    stop = watchEffect(() => {
      const flag = checkFunc()
      if (flag) {
        resolve()

        if (stop) {
          stop()
        } else {
          stopOnFirst = true
        }
      }
    })

    if (stopOnFirst) {
      stop()
    }
  })
}

async function to(promiseFunc: Promise<any>) {
  try {
    const result = await promiseFunc
    return [null, result]
  } catch (error) {
    return [error, null]
  }
}

function toArray<T = any>(value: T | T[] | undefined | null): T[] {
  if (value === undefined || value === null) {
    return []
  }
  return Array.isArray(value) ? value : [value]
}

function isIp(str: string) {
  if (str === '') {
    return false
  }
  const ips = str.split('.')
  if (ips.length !== 4) {
    return false
  }
  for (const ipSlice of ips) {
    if (!/^\d+$/.test(ipSlice)) {
      return false
    }
    if (Number(ipSlice) < 0 || Number(ipSlice) > 255) {
      return false
    }
  }
  return true
}

function isIpRange(strs: [string, string]) {
  return isIp(strs[0]) && isIp(strs[1])
}

function ipCheck(ip: string): boolean
function ipCheck(ipRange: [string, string]): boolean
function ipCheck(ipOrRange: string | [string, string]) {
  const check = (str: string) => {
    if (!str) {
      return true
    }
    const lastValueIndex =
      3 -
      str
        .split('.')
        .reverse()
        .findIndex((value) => !!value)
    const firstEmptyIndex = str.split('.').findIndex((value) => !value)
    return firstEmptyIndex === -1 || lastValueIndex < firstEmptyIndex
  }

  if (typeof ipOrRange === 'string') {
    return check(ipOrRange)
  } else {
    const [startIp, endIp] = ipOrRange
    return check(startIp) && check(endIp)
  }
}

function dateFormat(time: number | string, format = 'YYYY-MM-DD HH:mm:ss', emptyStr = '-') {
  if (typeof time === 'number') {
    return time === 0 || Number.isNaN(time) ? emptyStr : dayjs(time).format(format)
  }
  if (typeof time === 'string') {
    if (time === '' || time === '0001-01-01T00:00:00Z') {
      return emptyStr
    }
    return dayjs(time).format(format)
  }
  return emptyStr
}

function autoUnit(size: number, digits = 1, unit: 'B' | 'KB' | 'MB' | 'GB' | 'TB' | 'PB' = 'B'): string {
  if (typeof size !== 'number') { throw new TypeError('autoUnit size params is need number') }
  if (Number.isNaN(size)) {
    return ''
  }
  let negativeStr = ''
  if (size < 0) {
    negativeStr = '-'
    size = Math.abs(size)
  }

  const allUnits = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const checkUnit = allUnits.slice(allUnits.indexOf(unit))

  while (true) {
    const unitNow = checkUnit.shift()
    if (size < 1024 || checkUnit.length === 0) {
      return `${negativeStr}${size.toFixed(digits)}${unitNow}`
    }

    size /= 1024
  }
}


export const util = {
  uuid,
  sleep,
  wait,
  to,
  toArray,
  isIp,
  isIpRange,
  ipCheck,
  dateFormat,
  autoUnit,
}
